'use client';

import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { apiClient } from '@/services/api';
import { useNavigate } from 'react-router-dom';
import SubscriptionRecordsAdmin from '@/components/admin/SubscriptionRecordsAdmin';

// TypeScript 接口定义
interface Plan {
  id: number;
  code: string;
  name: string;
  description: string;
  price_cents: number;
  price_yuan: number;
  duration_days: number;
  duration_value?: number;
  duration_unit?: string;
  project_credits_total: number;
  max_users: number;
  is_trial: boolean;
  status: string;
  created_at: string;
  updated_at: string;
}

export default function PlansAdmin() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // 筛选状态
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'archived', 'all'
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    price_cents: '',
    price_yuan: '',
    duration_days: '',
    duration_value: '',
    duration_unit: '',
    project_credits_total: '',
    max_users: '',
    is_trial: false,
    status: 'active'
  });

  // 开通会员相关状态
  interface SearchedUser {
    id: number;
    username: string; // 手机号
    display_name?: string;
    role: string;
    organization_name?: string;
  }

  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedUserForSubscription, setSelectedUserForSubscription] = useState<SearchedUser | null>(null);
  const [users, setUsers] = useState<SearchedUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchPhone, setSearchPhone] = useState('');
  const [subscriptionError, setSubscriptionError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Snackbar状态（用于错误提示）
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const showErrorSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  useEffect(() => {
    fetchPlans();
  }, [statusFilter]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      console.log('开始获取套餐列表...');
      // 添加时间戳参数破坏缓存和状态筛选参数
      const timestamp = Date.now();
      const params = new URLSearchParams({
        t: timestamp.toString()
      });
      
      // 只有当筛选条件不是'all'时才添加status参数
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await apiClient.plans.getList(params);
      console.log('套餐API响应:', response);
      
      if (response.success && response.data) {
        console.log('套餐数据:', response.data);
        
        // 根据筛选条件处理数据
        let filteredPlans = response.data || [];
        
        // 如果筛选条件是'active'，过滤掉archived状态的套餐
        if (statusFilter === 'active') {
          filteredPlans = filteredPlans.filter((plan: Plan) => plan.status !== 'archived');
        }
        // 如果筛选条件是'archived'，只保留archived状态的套餐
        else if (statusFilter === 'archived') {
          filteredPlans = filteredPlans.filter((plan: Plan) => plan.status === 'archived');
        }
        // 如果筛选条件是'all'，保留所有套餐（不进行过滤）
        
        console.log('筛选条件:', statusFilter);
        console.log('筛选后的套餐数量:', filteredPlans.length);
        console.log('套餐详情:', filteredPlans.map((p: Plan) => ({ id: p.id, name: p.name, status: p.status, price_yuan: p.price_yuan, updated_at: p.updated_at })));
        
        setPlans(filteredPlans);
      } else {
        throw new Error(response.message || '获取套餐列表失败');
      }
    } catch (err) {
      setError('获取套餐列表失败');
      console.error('Error fetching plans:', err);
      setPlans([]); // 设置为空数组而不是演示数据
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (plan: Plan | null = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        code: plan.code,
        name: plan.name,
        description: plan.description,
        price_cents: plan.price_cents.toString(),
        price_yuan: plan.price_yuan.toString(),
        duration_days: plan.duration_days.toString(),
        duration_value: plan.duration_value?.toString() || '',
        duration_unit: plan.duration_unit || '',
        project_credits_total: plan.project_credits_total.toString(),
        max_users: plan.max_users.toString(),
        is_trial: plan.is_trial,
        status: plan.status
      });
    } else {
      setEditingPlan(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        price_cents: '',
        price_yuan: '',
        duration_days: '',
        duration_value: '',
        duration_unit: '',
        project_credits_total: '',
        max_users: '',
        is_trial: false,
        status: 'active'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPlan(null);
  };

  const handleSubmit = async () => {
    try {
      // 准备提交数据，自动计算price_cents
      const submitData = {
        ...formData,
        price_cents: Number(formData.price_yuan || 0) * 100, // 将元转换为分
        price_yuan: Number(formData.price_yuan || 0),
        duration_days: Number(formData.duration_days || 0),
        duration_value: Number(formData.duration_value || 0),
        project_credits_total: Number(formData.project_credits_total || 0),
        max_users: Number(formData.max_users || 1)
      };
      
      if (editingPlan) {
        const response = await apiClient.plans.update(editingPlan.id.toString(), submitData);
        if (!response.success) {
          throw new Error(response.message || '更新套餐失败');
        }
      } else {
        const response = await apiClient.plans.create(submitData);
        if (!response.success) {
          throw new Error(response.message || '创建套餐失败');
        }
      }
      
      await fetchPlans();
      handleCloseDialog();
      setError('');
    } catch (err) {
      setError(editingPlan ? '更新套餐失败' : '创建套餐失败');
      console.error('Error saving plan:', err);
    }
  };

  // 开通会员相关函数
  const handleOpenSubscriptionDialog = async () => {
    setSubscriptionDialogOpen(true);
    setSelectedUserForSubscription(null);
    setSearchPhone('');
    setUsers([]);
    setSubscriptionError('');
    setSelectedPlan(null);
    setPlansLoading(true);

    try {
      // 获取可售卖套餐（激活、非试用、价格>0）
      const res = await apiClient.plans.getList({ status: 'active' });
      if (!res.success) throw new Error(res.message || '加载套餐失败');
      const rows: Plan[] = (res.data || []).filter((p: Plan) => {
        const sellable = !p.is_trial;
        const hasPrice = Number(p.price_yuan || 0) > 0 || Number(p.price_cents || 0) > 0;
        return sellable && hasPrice;
      }).sort((a: any, b: any) => (a.price_yuan || 0) - (b.price_yuan || 0));
      setSubscriptionPlans(rows);
    } catch (err: any) {
      console.error('获取套餐列表失败:', err);
      showErrorSnackbar('获取套餐列表失败');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleCloseSubscriptionDialog = () => {
    setSubscriptionDialogOpen(false);
    setSelectedUserForSubscription(null);
    setSearchPhone('');
    setUsers([]);
    setSubscriptionError('');
    setSelectedPlan(null);
    setHasSearched(false);
  };

  const handleSearchUsers = async () => {
    if (!searchPhone.trim()) {
      setUsers([]);
      setHasSearched(false);
      return;
    }
    setUsersLoading(true);
    setSubscriptionError('');
    setHasSearched(true);
    try {
      console.log('搜索用户:', searchPhone.trim());
      const resp = await apiClient.get('/users/search-for-subscription', { search: searchPhone.trim() });
      console.log('用户搜索响应:', resp);
      if (!resp.success) throw new Error(resp.message || '搜索用户失败');
      setUsers(resp.data?.users || []);
    } catch (err: any) {
      console.error('搜索用户失败:', err);
      setSubscriptionError(err.message || '搜索用户失败');
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUserSelect = (user: SearchedUser) => {
    setSelectedUserForSubscription(user);
    setSearchPhone(user.username);
  };

  const handlePlanSelect = (plan: Plan) => setSelectedPlan(plan);

  const handleCreateSubscription = async (planId: number) => {
    if (!selectedUserForSubscription) return;
    setUsersLoading(true);
    setSubscriptionError('');
    try {
      const response = await apiClient.admin.createSubscription({
        user_id: selectedUserForSubscription.id,
        plan_id: planId,
      });
      if (response.success) {
        handleCloseSubscriptionDialog();
        // 刷新套餐列表以反映可能的变化
        fetchPlans();
      } else {
        showErrorSnackbar(response.message || '开通会员失败');
      }
    } catch (err) {
      console.error('开通会员失败:', err);
      const message = (err as any)?.message || '开通会员失败';
      showErrorSnackbar(message);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleDelete = async (planId: number) => {
    if (!window.confirm('确定要删除这个套餐吗？')) {
      return;
    }

    try {
      const response = await apiClient.plans.delete(planId.toString());
      if (!response.success) {
        throw new Error(response.message || '删除套餐失败');
      }
      await fetchPlans();
      setError('');
    } catch (err) {
      setError('删除套餐失败');
      console.error('Error deleting plan:', err);
    }
  };


  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>加载中...</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">套餐管理</Typography>
        {activeTab === 0 && (
          <Box display="flex" gap={2}>
            <Button 
              variant="contained" 
              startIcon={<StarIcon />}
              onClick={handleOpenSubscriptionDialog}
              color="primary"
            >
              开通会员
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              color="primary"
            >
              创建套餐
            </Button>
            <Button onClick={fetchPlans}>刷新</Button>
          </Box>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="套餐管理" />
          <Tab label="订阅记录" />
        </Tabs>
      </Box>

      {error && activeTab === 0 && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {activeTab === 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            状态筛选：
          </Typography>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="active">生效中</MenuItem>
            <MenuItem value="archived">已归档</MenuItem>
            <MenuItem value="all">全部</MenuItem>
          </Select>
          <Typography variant="body2" color="text.secondary">
            共 {plans.length} 个套餐
          </Typography>
        </Box>
      )}

      {activeTab === 0 ? (
        loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>编码</TableCell>
                <TableCell>名称</TableCell>
                <TableCell>价格(元)</TableCell>
                <TableCell>周期数值</TableCell>
                <TableCell>单位</TableCell>
                <TableCell>项目额度</TableCell>
                <TableCell>最大用户数</TableCell>
                <TableCell>试用</TableCell>
                <TableCell>状态</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell>{plan.id}</TableCell>
                  <TableCell>{plan.code}</TableCell>
                  <TableCell>{plan.name}</TableCell>
                  <TableCell>{plan.price_yuan || Math.round((plan.price_cents || 0)/100)}</TableCell>
                  <TableCell>{plan.duration_value ?? ''}</TableCell>
                  <TableCell>
                    {plan.duration_unit ? (
                      plan.duration_unit === 'day' ? '天' :
                      plan.duration_unit === 'week' ? '周' :
                      plan.duration_unit === 'month' ? '月' :
                      plan.duration_unit === 'quarter' ? '季度' :
                      plan.duration_unit === 'year' ? '年' : plan.duration_unit
                    ) : ''}
                  </TableCell>
                  <TableCell>{plan.project_credits_total}</TableCell>
                  <TableCell>{plan.max_users || 1}</TableCell>
                  <TableCell>{plan.is_trial ? '是' : '否'}</TableCell>
                  <TableCell>
                    {plan.status === 'active' ? '激活' : plan.status === 'archived' ? '已归档' : plan.status}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenDialog(plan)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(plan.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      ) : (
        <SubscriptionRecordsAdmin />
      )}

      {/* 编辑对话框 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingPlan ? '编辑套餐' : '创建套餐'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
              <TextField
                label="编码"
                value={formData.code || ''}
                onChange={(e) => handleInputChange('code', e.target.value)}
                fullWidth sx={{ mb: 2 }}
              />
              <TextField
                label="名称"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                fullWidth sx={{ mb: 2 }}
              />
              <TextField
                label="价格(元)"
                type="number"
                value={formData.price_yuan || 0}
                onChange={(e) => handleInputChange('price_yuan', e.target.value)}
                fullWidth sx={{ mb: 2 }}
              />
              {/* 周期：单位+数值，兼容旧days字段 */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  label="周期数值"
                  type="number"
                  value={formData.duration_value || ''}
                  onChange={(e) => handleInputChange('duration_value', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Select
                  value={formData.duration_unit || ''}
                  displayEmpty
                  onChange={(e) => handleInputChange('duration_unit', e.target.value)}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value=""><em>选择单位</em></MenuItem>
                  <MenuItem value="day">天</MenuItem>
                  <MenuItem value="week">周</MenuItem>
                  <MenuItem value="month">月</MenuItem>
                  <MenuItem value="quarter">季度</MenuItem>
                  <MenuItem value="year">年</MenuItem>
                </Select>
              </Box>
              <TextField
                label="周期(天，兼容旧数据，不填)"
                type="number"
                value={formData.duration_days || 0}
                onChange={(e) => handleInputChange('duration_days', e.target.value)}
                fullWidth sx={{ mb: 2 }}
              />
              <TextField
                label="项目额度"
                type="number"
                value={formData.project_credits_total || 0}
                onChange={(e) => handleInputChange('project_credits_total', e.target.value)}
                fullWidth sx={{ mb: 2 }}
              />
              <TextField
                label="最大用户数"
                type="number"
                value={formData.max_users || 1}
                onChange={(e) => handleInputChange('max_users', e.target.value)}
                fullWidth sx={{ mb: 2 }}
              />
              <Select
                value={formData.is_trial ? 1 : 0}
                onChange={(e) => handleInputChange('is_trial', e.target.value === 1)}
                fullWidth sx={{ mb: 2 }}
              >
                <MenuItem value={1}>试用</MenuItem>
                <MenuItem value={0}>非试用</MenuItem>
              </Select>
              <Select
                value={formData.status || 'active'}
                onChange={(e) => handleInputChange('status', e.target.value)}
                fullWidth
              >
                <MenuItem value="active">激活</MenuItem>
                <MenuItem value="archived">已归档</MenuItem>
              </Select>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button variant="contained" onClick={handleSubmit}>保存</Button>
        </DialogActions>
      </Dialog>

      {/* 开通会员对话框 */}
      <Dialog open={subscriptionDialogOpen} onClose={handleCloseSubscriptionDialog} fullWidth maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon color="primary" />
            <Typography variant="h6" component="span">
              {selectedUserForSubscription ? `为 ${selectedUserForSubscription.display_name || selectedUserForSubscription.username} 开通会员` : '开通会员'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* 用户搜索 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              1. 选择用户
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="输入手机号搜索管理员用户"
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onKeyPress={(e) => {
                  if ((e as any).key === 'Enter') {
                    handleSearchUsers();
                  }
                }}
              />
              <Button 
                variant="outlined" 
                onClick={handleSearchUsers}
                disabled={!searchPhone.trim() || usersLoading}
              >
                {usersLoading ? <CircularProgress size={20} /> : '搜索'}
              </Button>
            </Box>

            {/* 用户列表 */}
            {users.length > 0 && (
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {users.map((user) => (
                  <Box
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      borderBottom: 1,
                      borderColor: 'divider',
                      backgroundColor: selectedUserForSubscription?.id === user.id ? 'action.selected' : 'transparent',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {user.display_name || user.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      手机号: {user.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      公司: {user.organization_name || '未分配'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {hasSearched && users.length === 0 && !usersLoading && (
              <Alert severity="info">未找到匹配的用户</Alert>
            )}
          </Box>

          {/* 套餐选择 */}
          {selectedUserForSubscription && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                2. 选择套餐
              </Typography>
              {plansLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {Array.isArray(subscriptionPlans) && subscriptionPlans.length > 0 ? subscriptionPlans.map((plan) => (
                    <Card
                      key={plan.id}
                      onClick={() => handlePlanSelect(plan)}
                      sx={{
                        cursor: 'pointer',
                        border: selectedPlan?.id === plan.id ? 2 : 1,
                        borderColor: selectedPlan?.id === plan.id ? 'primary.main' : 'divider',
                        transition: 'all 0.2s ease-in-out',
                        position: 'relative',
                        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" fontWeight="bold">{plan.name}</Typography>
                            {plan.description && (
                              <Typography variant="body2" color="text.secondary">{plan.description}</Typography>
                            )}
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h4" color="primary" fontWeight="bold">¥{Number(plan.price_yuan || 0).toLocaleString()}</Typography>
                            <Typography variant="body2" color="text.secondary">{(plan.duration_value && plan.duration_unit) ? `${plan.duration_value}${plan.duration_unit}` : ''}</Typography>
                          </Box>
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                            项目数量：{plan.project_credits_total > 10000 ? '不限制' : `${plan.project_credits_total}个`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                            账号数量：{plan.max_users && plan.max_users > 10000 ? '不限制' : `${plan.max_users || 1}个`}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  )) : (
                    <Alert severity="info">暂无可用的套餐</Alert>
                  )}
                </Box>
              )}
            </Box>
          )}

          {selectedUserForSubscription && (
            <Alert severity="info">开通后将为该用户所在公司创建订单记录，支付方式记录为对公转账。</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubscriptionDialog} size="large">取消</Button>
          {selectedUserForSubscription && selectedPlan && (
            <Button
              variant="contained"
              size="large"
              onClick={() => handleCreateSubscription(selectedPlan.id)}
              disabled={usersLoading}
              startIcon={<StarIcon />}
            >
              {usersLoading ? '开通中...' : `为 ${selectedUserForSubscription.display_name || selectedUserForSubscription.username} 开通 ${selectedPlan.name}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 错误提示Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
}